<script lang="ts" setup>
import type { SparkleConfig } from './config';

import { computed, onBeforeUnmount, useTemplateRef, watch } from 'vue';

const {
	onLoopAnchorChange = null,
	sparkle,
} = defineProps<{
	sparkle: SparkleConfig;
	animating: boolean;
	onLoopAnchorChange?: ((element: SVGAnimationElement | null) => void) | null;
}>();

const loopAnchor = useTemplateRef<SVGAnimationElement>('loopAnchor');
const translateAnimation = useTemplateRef<SVGAnimateTransformElement>('translateAnimation');
const scaleAnimation = useTemplateRef<SVGAnimateTransformElement>('scaleAnimation');
const pathAnimation = useTemplateRef<SVGAnimationElement>('pathAnimation');
const idleUsesStroke = computed(() => sparkle.idleDisplay === 'stroke');

function beginAnimations() {
	loopAnchor.value?.beginElement();
	translateAnimation.value?.beginElement();
	scaleAnimation.value?.beginElement();
	pathAnimation.value?.beginElement();
}

function syncLoopAnchor(element: SVGAnimationElement | null) {
	if (onLoopAnchorChange === null) return;

	onLoopAnchorChange(element);
}

watch(loopAnchor, (element) => syncLoopAnchor(element), { immediate: true });

onBeforeUnmount(() => {
	if (onLoopAnchorChange === null) return;

	onLoopAnchorChange(null);
});

defineExpose({
	beginAnimations,
});
</script>

<template>
	<g :transform="sparkle.groupTransform">
		<animate
			v-if="animating"
			ref="loopAnchor"
			attributeName="opacity"
			begin="indefinite"
			data-begin="manual"
			dur="1s"
			:keyTimes="sparkle.opacity.keyTimes"
			repeatCount="indefinite"
			:values="sparkle.opacity.values"
		/>
		<animateTransform
			v-if="animating"
			ref="translateAnimation"
			attributeName="transform"
			begin="indefinite"
			data-begin="manual"
			dur="1s"
			:keyTimes="sparkle.translate.keyTimes"
			repeatCount="indefinite"
			type="translate"
			:values="sparkle.translate.values"
		/>
		<g transform="translate(12 12)">
			<g :transform="sparkle.scale.base">
				<animateTransform
					v-if="animating"
					ref="scaleAnimation"
					attributeName="transform"
					begin="indefinite"
					data-begin="manual"
					dur="1s"
					:keyTimes="sparkle.scale.keyTimes"
					repeatCount="indefinite"
					type="scale"
					:values="sparkle.scale.values"
				/>
				<g transform="translate(-12 -12)">
					<path
						v-if="animating === false"
						:class="idleUsesStroke ? 'fill-none stroke-current' : 'fill-current'"
						:d="sparkle.idlePath"
						:fill-rule="idleUsesStroke ? undefined : 'evenodd'"
						:stroke-linecap="idleUsesStroke ? 'round' : undefined"
						:stroke-linejoin="idleUsesStroke ? 'round' : undefined"
						:stroke-width="idleUsesStroke ? '2' : undefined"
					/>
					<g v-else>
						<path class="fill-current" :d="sparkle.animatedPathBase" fill-rule="evenodd">
							<animate
								ref="pathAnimation"
								attributeName="d"
								begin="indefinite"
								data-begin="manual"
								dur="1s"
								:keyTimes="sparkle.animatedPath.keyTimes"
								repeatCount="indefinite"
								:values="sparkle.animatedPath.values"
							/>
						</path>
						<path
							v-if="sparkle.animatedStrokePath"
							class="fill-none stroke-current"
							:d="sparkle.animatedStrokePath"
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
						/>
					</g>
				</g>
			</g>
		</g>
	</g>
</template>
